<template>
    <VideoPlayer
        v-if="videoSrc"
        ref="video"
        :key="videoSrc"
        :src="videoSrc"
        @videoLoaded="onBannerLoaded"
        @updateTime="onUpdateTime"
    />
</template>

<script lang="ts">
import { defineComponent } from "vue";
import equal from "fast-deep-equal";
import type { AEMasterTemplate } from "hoxton-types";

import type { ComputedOverwrite } from "@/types/types";
import { PlaybackBannerActions, PlaybackBannerEvents } from "@/components/PlaybackControls/usePlaybackController";
import VideoPlayer from "@/components/Previews/VideoPlayer.vue";
import bus from "@/bus";
import { BannerEvents } from "@/enums/banners";
import { AERenderService, PLAINLY_POLL_INTERVAL } from "@/utils/ae";
import type { PlainlyLayerManagement } from "@/utils/CreativeIntelligenceApi";

export default defineComponent({
    components: {
        VideoPlayer
    },

    props: {
        id: {
            required: true,
            type: String
        },
        previewValues: {
            required: true,
            type: String
        },
        editorValues: {
            default: () => ({}),
            type: Object
        },
        playbackNamespace: {
            type: String,
            required: true
        }
    },

    emits: [BannerEvents.BannerLoaded, BannerEvents.BannerError, "loading", "bannerError"],

    data(): {
        videoSrc: string | null;
        renderPendingTimeoutId: ReturnType<typeof setTimeout> | null;
    } {
        return {
            videoSrc: null,
            renderPendingTimeoutId: null
        };
    },

    computed: {
        data(): {
            masterTemplate: AEMasterTemplate;
            computedOverwrites: ComputedOverwrite[];
            reportingLabel: string;
        } {
            return JSON.parse(this.previewValues);
        },

        liveComputedOverwrites(): ComputedOverwrite[] {
            return this.data.computedOverwrites.map(overwrite => {
                const newValue = this.editorValues[overwrite.name]?.value || overwrite.newValue;
                const settings = this.editorValues[overwrite.name]?.settings || overwrite?.settings;

                const computedData = {
                    ...overwrite,
                    newValue
                };

                if (settings) {
                    const layerManagement = {} as PlainlyLayerManagement;

                    if (settings.font) {
                        layerManagement.font = settings.font;
                    }

                    if (settings.fontSize) {
                        layerManagement.fontSize = settings.fontSize;
                    }

                    if (Object.keys(layerManagement).length) {
                        computedData.layerManagement = layerManagement;
                    }
                }

                return computedData;
            });
        },

        clientId() {
            return this.$route.params.clientId as string;
        },

        AERenderService() {
            return AERenderService({ clientId: this.clientId });
        }
    },

    watch: {
        editorValues(newValue, oldValue) {
            if (equal(newValue, oldValue)) {
                return;
            }

            this.requestAEPreview();
        },

        data(newValue, oldValue) {
            if (equal(newValue, oldValue)) {
                return;
            }

            if (
                newValue?.masterTemplate?.id !== oldValue?.masterTemplate?.id ||
                (!oldValue?.masterTemplate?.aeInfo?.completed && newValue?.masterTemplate?.aeInfo?.completed)
            ) {
                this.requestAEPreview();
            }
        }
    },

    created() {
        bus.$emit(PlaybackBannerEvents.Init, { namespace: this.playbackNamespace, id: this.id });
    },

    mounted() {
        bus.$on(PlaybackBannerActions.ReplayBanner, this.onReplay);

        bus.$on(PlaybackBannerActions.Play, this.onPlay);
        bus.$on(PlaybackBannerActions.Pause, this.onPause);
        bus.$on(PlaybackBannerActions.Seek, this.onSeek);

        this.requestAEPreview();
    },

    beforeUnmount() {
        if (this.renderPendingTimeoutId) {
            clearTimeout(this.renderPendingTimeoutId);
        }

        this.AERenderService.clear();

        bus.$off(PlaybackBannerActions.ReplayBanner, this.onReplay);

        bus.$off(PlaybackBannerActions.Play, this.onPlay);
        bus.$off(PlaybackBannerActions.Pause, this.onPause);
        bus.$off(PlaybackBannerActions.Seek, this.onSeek);
    },

    methods: {
        onReplay({ namespace, id }: { namespace: string; id?: string }) {
            if (id !== undefined && id !== this.id) {
                return;
            }

            if (namespace !== this.playbackNamespace) {
                return;
            }

            this.seek(0);
            this.play();
        },

        onPlay({ namespace, id }: { namespace: string; id?: string }) {
            if (id !== undefined && id !== this.id) {
                return;
            }

            if (namespace !== this.playbackNamespace) {
                return;
            }

            this.play();
        },

        onPause({ namespace, id }: { namespace: string; id?: string }) {
            if (id !== undefined && id !== this.id) {
                return;
            }

            if (namespace !== this.playbackNamespace) {
                return;
            }

            this.pause();
        },

        onSeek({ namespace, time, id }: { namespace: string; time: number; id?: string }) {
            if (id !== undefined && id !== this.id) {
                return;
            }

            if (namespace !== this.playbackNamespace) {
                return;
            }

            this.seek(time);
        },

        play() {
            if (this.$refs.video) {
                (this.$refs.video as typeof VideoPlayer).play();
            }
        },

        pause() {
            if (this.$refs.video) {
                (this.$refs.video as typeof VideoPlayer).pause();
            }
        },

        seek(time: number) {
            if (this.$refs.video) {
                (this.$refs.video as typeof VideoPlayer).seek(time);
            }
        },

        onBannerLoaded(duration: number) {
            this.$emit(BannerEvents.BannerLoaded);

            bus.$emit(PlaybackBannerEvents.SetBannerDuration, {
                namespace: this.playbackNamespace,
                id: this.id,
                duration
            });

            this.play();
        },

        onUpdateTime(currentTime: number) {
            bus.$emit(PlaybackBannerEvents.SetBannerTime, {
                namespace: this.playbackNamespace,
                id: this.id,
                currentTime
            });
        },

        async requestAEPreview() {
            if (this.renderPendingTimeoutId) {
                clearTimeout(this.renderPendingTimeoutId);
            }

            const {
                masterTemplate: { aeInfo, reportingLabel: masterTemplateReportingLabel },
                reportingLabel
            } = this.data;

            if (!aeInfo.completed) {
                return;
            }

            this.renderPendingTimeoutId = setTimeout(async () => {
                this.$emit("loading", true);

                try {
                    const requestId = this.renderPendingTimeoutId;

                    const url = await this.AERenderService.requestRender({
                        reportingLabel: reportingLabel || masterTemplateReportingLabel,
                        computedOverwrites: this.liveComputedOverwrites,
                        aeInfo,
                        preview: true
                    });

                    if (requestId !== this.renderPendingTimeoutId) {
                        return;
                    }

                    this.videoSrc = url;
                } catch (error) {
                    if (typeof error === "string") {
                        this.$snackbar.error(error);
                        this.$emit(BannerEvents.BannerError, error);
                    } else if (error instanceof Error) {
                        this.$snackbar.error(error.message, "", {
                            duration: 10000,
                            closable: true
                        });
                        this.$emit(BannerEvents.BannerError, error.message);
                    }
                } finally {
                    this.$emit("loading", false);
                }
            }, PLAINLY_POLL_INTERVAL);
        }
    }
});
</script>

<style lang="scss" scoped>
.ae-banner-video {
    width: 100%;
    height: 100%;
}
</style>
